import { Component, Input, forwardRef } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { SearchIconSvg } from '@g99-product-web-ui/shared-util-lib';

@Component({
  selector: 'lib-input',
  templateUrl: './input.component.html',
  styleUrl: './input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class InputComponent implements ControlValueAccessor {
  @Input() public placeholder = ''; // Placeholder for input
  @Input() public label = ''; // Placeholder for input
  @Input() public labelout = ''; // Placeholder for input
  @Input() public inputId = ''; // ID for the input field
  @Input() public name = ''; // Name for the input field
  @Input() public isInvalid = false; // Error state
  @Input() public height = '40px'; // Error state
  @Input() public errorMessage: { [key: string]: string } = {}; // Error messages
  @Input() public formControls!: AbstractControl | null;
  @Input() public inputType: 'password' | 'text' | 'number' = 'text'; // Defines if the input is password or text
  /**
   * Overrides the browser autocomplete hint. Unset keeps the default ('on' for passwords, 'off' otherwise).
   * Pass 'new-password' for a secret that is not the user's own password (e.g. a webhook signing secret),
   * so the browser does not autofill their saved login into it.
   */
  @Input() public autocomplete: string | null = null;
  @Input() public isReadonly = false; // Flag to control input's readonly state
  @Input() public isModeAutocomplete = false;
  @Input() public addressOptionCountry: any = {
    ComponentRestrictions: {
      country: ['US'],
    },
  };

  @Input() public disabled = false;
  @Input() public isSearch = false;
  @Input() public prefixText = '';
  public value = ''; // Value for FormControl
  public showPassword = false;
  public searchIcon = SearchIconSvg;

  // Toggles the password visibility
  public togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  public onChange: (value: string) => void = (value: string) => {
    // Invoke onChange callback if it has been registered
    if (this.onChange) {
      this.onChange(value);
    }
  };

  public onTouched: () => void = () => {
    // Invoke onTouched callback if it has been registered
    if (this.onTouched) {
      this.onTouched();
    }
  };
  public onInputFocus(): void {
    this.onTouched();
  }
  // ControlValueAccessor implementation
  public writeValue(value: string): void {
    this.value = value;
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  public onInputChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.value = target.value;
    this.onChange(this.value); // Notify value change
    this.onTouched(); // Mark as touched
  }
  public getErrorMessage(): string | null {
    if (
      this.formControls &&
      this.formControls.invalid &&
      (this.formControls.dirty || this.formControls.touched)
    ) {
      const errors = this.formControls.errors;
      if (errors) {
        for (const errorKey in errors) {
          if (this.errorMessage[errorKey]) {
            return this.errorMessage[errorKey];
          }
        }
      }
    }
    return null;
  }

  public clearInput(): void {
    this.value = '';
    this.onChange(this.value);
  }
}
